import { Product } from "@/api/product-api";
import useUser from "@/api/apis";
import {
  OTHER_CHIPS_MAX,
  OTHER_TEXT_MAX_LEN,
  useRecommendTactSuitStore,
} from "@/components/home/RecommendTactSuitUtil";
import { cn } from "@/libs/utils";
import { useRouter } from "next/router";
import { useTrans } from "@/utils/useTrans";
import { Badge } from "@/v2-components/ui/badge";
import { Button, ButtonProps } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import CloseIcon from "@/v2-components/core/icon/close-icon";
import StepCard from "@/v2-components/StepCard";
import TactSuitCard from "@/v2-components/TactSuitCard";
import Link from "next/link";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { useStaggerAnimation } from "@/hooks/useStaggerAnimation";

const BASE_PATH = "/images/home/recommend_tactsuit";

export default function RecommendTactSuit({
  disableAnimation,
  renderTrigger,
}: {
  disableAnimation?: boolean;
  /**
   * 팝업을 여는 요소를 직접 그리고 싶을 때 쓴다. 안 넘기면 기존 인트로 배너가 그대로 나온다.
   * (홈 리뉴얼 시안은 배너 없이 "제품 추천받기" 버튼 하나만 둔다.)
   */
  renderTrigger?: (open: () => void) => React.ReactNode;
}) {
  const { stepIndex, clear } = useRecommendTactSuitStore();
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);

  return (
    <Fragment>
      {open && (
        <Dialog open={open}>
          <DialogTitle className="sr-only">Recommend TactSuit</DialogTitle>
          {/* ⚠️ 다이얼로그 **자체는 스크롤하지 않는다**(`overflow-hidden` 유지). 내용이
              `max-h`(90dvh)를 넘을 때 스크롤되어야 하는 건 **중간 섹션뿐**이고, 닫기(X)·스텝
              표시·하단 결정 버튼은 제자리에 남아야 한다. 그래서 각 스텝이 헤더/본문/푸터
              3영역으로 나뉘고 본문만 `flex-1 min-h-0 overflow-y-auto` 를 갖는다.
              (여기에 `overflow-y-auto` 를 걸면 X 와 결정 버튼이 같이 밀려 올라간다.) */}
          <DialogContent
            aria-describedby="Recommend TactSuit Content"
            className="flex h-auto max-h-[calc(90dvh)] max-w-screen-820 flex-col overflow-hidden"
          >
            <DialogClose asChild className="flex w-full justify-end">
              <button
                onClick={() => {
                  // clear();
                  // setReady(false);
                  setOpen(false);
                }}
              >
                <CloseIcon className="size-6" />
              </button>
            </DialogClose>
            {stepIndex === 0 && !ready && <SelectInterests />}
            {stepIndex === 1 && !ready && <SelectGenres />}
            {stepIndex === 2 && !ready && (
              <SelectFeatures onReady={() => setReady(true)} />
            )}
            {ready && <SuggestProducts onClose={() => setReady(false)} />}
          </DialogContent>
        </Dialog>
      )}
      {(() => {
        const openPopup = () => {
          clear();
          setReady(false);
          setOpen(true);
        };
        return renderTrigger ? (
          renderTrigger(openPopup)
        ) : (
          <Intro disableAnimation={disableAnimation} onClick={openPopup} />
        );
      })()}
    </Fragment>
  );
}

// 첫번째
const SelectInterests = () => {
  const {
    next,
    steps,
    toggleOption,
    setOtherInputText,
    commitOtherChip,
    removeOtherChip,
    isOtherOption,
    someCurrentStepSelected,
  } = useRecommendTactSuitStore();
  const { t } = useTrans(["gnb"]);
  const otherOption = steps[0].options.find((o) => isOtherOption(o.id));

  return (
    <StepCard
      numStages={3}
      currentStage={0}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="mt-6 shrink-0 laptop:mt-12">
        <StepCard.Stepper />
        <Label className="mt-11 laptop:mt-16">
          {t("What are your interests?")}
        </Label>
        <Description className="mt-3 laptop:mt-4">
          {t("Select all that apply")}
        </Description>
      </div>
      {/* 스크롤 영역 = 선택지 + Other 칩 입력. 칩 입력을 **이 안에** 둔다 —
          바깥 형제로 두면 `min-h-0` 이 붙은 이 블록만 수축 가능해서 음수 공간을 전부
          흡수한다(실측 360x568·칩 4개: 본문이 248 → 59px 로 짜부라짐). 칩 입력은
          "선택" UI 라 선택지와 함께 스크롤되는 게 의미상으로도 맞다. */}
      <div className="mt-10 flex min-h-0 flex-1 flex-col overflow-y-auto laptop:mt-16">
        <div className="flex flex-wrap gap-3">
          {steps[0].options.map((option) => {
            return (
              <Capsule
                key={option.id}
                label={t(option.label)}
                selected={option.selected}
                onClick={(e) => {
                  toggleOption(option.id);
                }}
              />
            );
          })}
        </div>
        {otherOption?.selected && (
          <OtherChipsInput
            chips={otherOption.otherTexts ?? []}
            inputText={otherOption.otherInputText ?? ""}
            onInputChange={(v) => setOtherInputText(otherOption.id, v)}
            onCommit={() => commitOtherChip(otherOption.id)}
            onRemoveChip={(i) => removeOtherChip(otherOption.id, i)}
          />
        )}
      </div>
      {/* ⚠️ `flex-wrap` 필수. `justify-end` + nowrap 이면 버튼 합계가 푸터 폭을 넘을 때
          왼쪽 버튼이 **다이얼로그 밖으로 밀려 잘린다** — 실측(de, 390px): 푸터 310 vs
          버튼 115+12+227=354 → 첫 버튼이 left -4(다이얼로그 좌단 20)로 나가 안 눌린다.
          `gap-x-3` 이 아니라 `gap-3` 인 것도 이 때문 — row-gap 이 없으면 줄바꿈된 두 줄이 붙는다.

          ⚠️ 다만 `flex-wrap` 은 **합계 초과만** 풀고 **단일 버튼 초과는 못 푼다.** 가용폭은
          `w-[calc(100%-40px)]` + `p-5` 라 뷰포트 − 80 이고, 최장 단일 버튼은 de
          `Detaillierten Vergleich anzeigen`(실측 227px) 이다:
            390px → 가용 310, 여유 83   /   320px → 가용 240, 여유 **13**
          라벨이 조금 길어지거나 폰트가 커지면 그 버튼이 바로 다이얼로그 밖으로 나가고
          `flex-wrap` 은 막아주지 않는다. 라벨을 늘릴 때 320px 을 확인할 것.
          (최장 로케일은 푸터마다 다르다 — 단계 푸터는 es, 추천결과 푸터는 de.) */}
      <div className="mt-10 flex shrink-0 flex-wrap items-center justify-end gap-3">
        <StepCard.Next disabled={!someCurrentStepSelected} onClick={next} />
      </div>
    </StepCard>
  );
};

/**
 * Other 옵션 선택 시 노출되는 칩 입력 영역.
 * - Enter / 쉼표(,) 입력 시 input text 가 trim 되어 새 칩으로 commit.
 * - input 이 비어있는 상태에서 Backspace → 마지막 칩 제거.
 * - 각 칩 우측 × 클릭 → 해당 칩 제거.
 * - 64자/카테고리당 [OTHER_CHIPS_MAX] 개 상한. 빈/중복은 silent ignore.
 */
const OtherChipsInput = ({
  chips,
  inputText,
  onInputChange,
  onCommit,
  onRemoveChip,
}: {
  chips: string[];
  inputText: string;
  onInputChange: (v: string) => void;
  onCommit: () => void;
  onRemoveChip: (index: number) => void;
}) => {
  const { t } = useTrans(["gnb"]);
  const atLimit = chips.length >= OTHER_CHIPS_MAX;

  return (
    <div className="mt-4 laptop:mt-6">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-coolgray-20 bg-white px-3 py-2 transition-colors focus-within:border-blue-60">
        {chips.map((chip, idx) => (
          <span
            key={`${chip}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full bg-blue-10 px-3 py-1 text-sm text-coolgray-90"
          >
            <span className="break-all">{chip}</span>
            <button
              type="button"
              onClick={() => onRemoveChip(idx)}
              aria-label={`Remove ${chip}`}
              className="text-coolgray-60 hover:text-coolgray-90"
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputText}
          disabled={atLimit && inputText.length === 0}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            // 한글/일본어/중국어 IME 조합 중 Enter 는 조합 완료 신호이므로 commit 으로 처리하지 않는다.
            // (keyCode 229 또는 isComposing — 브라우저별 호환 위해 둘 다 체크)
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              onCommit();
            } else if (
              e.key === "Backspace" &&
              inputText.length === 0 &&
              chips.length > 0
            ) {
              e.preventDefault();
              onRemoveChip(chips.length - 1);
            }
          }}
          maxLength={OTHER_TEXT_MAX_LEN}
          placeholder={
            atLimit ? t("Maximum entries reached") : t("Please specify (Other)")
          }
          aria-label={t("Please specify (Other)")}
          className="min-w-[120px] flex-1 bg-transparent text-sm text-coolgray-90 outline-none placeholder:text-coolgray-50 disabled:cursor-not-allowed"
        />
      </div>
      <p className="mt-1 text-xs text-coolgray-60">
        {t("Press Enter or comma to add (up to N items)").replace(
          "N",
          String(OTHER_CHIPS_MAX),
        )}
      </p>
    </div>
  );
};

const Capsule = ({
  label,
  selected,
  onClick,
  className,
}: {
  selected: boolean;
  onClick: (e: any) => void;
  label: string;
  className?: string;
}) => {
  return (
    <Button
      variant={"blue-outline"}
      shape={"pill"}
      onClick={onClick}
      className={cn(
        "min-h-0 px-5 py-3 text-sm text-coolgray-90 laptop:px-6 laptop:py-4",
        selected ? "bg-blue-10" : "bg-white outline-coolgray-20",
        className,
      )}
    >
      {label}
    </Button>
  );
};

// 두번째
const SelectGenres = () => {
  const {
    next,
    steps,
    prev,
    toggleOption,
    setOtherInputText,
    commitOtherChip,
    removeOtherChip,
    isOtherOption,
    someCurrentStepSelected,
  } = useRecommendTactSuitStore();
  const { t } = useTrans(["gnb"]);
  const otherOption = steps[1].options.find((o) => isOtherOption(o.id));

  return (
    <StepCard
      numStages={3}
      currentStage={1}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="mt-6 shrink-0 laptop:mt-12">
        <StepCard.Stepper />
        <Label className="mt-11 laptop:mt-16">
          {t("What genres do you prefer?")}
        </Label>
        <Description className="mt-3 laptop:mt-4">
          {t("Select all that apply")}
        </Description>
      </div>
      {/* 스크롤 영역 = 선택지 + Other 칩 입력. 칩 입력을 **이 안에** 둔다 —
          바깥 형제로 두면 `min-h-0` 이 붙은 이 블록만 수축 가능해서 음수 공간을 전부
          흡수한다(실측 360x568·칩 4개: 본문이 248 → 59px 로 짜부라짐). 칩 입력은
          "선택" UI 라 선택지와 함께 스크롤되는 게 의미상으로도 맞다. */}
      <div className="mt-10 flex min-h-0 flex-1 flex-col overflow-y-auto laptop:mt-16">
        <div className="flex flex-wrap gap-3">
          {steps[1].options.map((option) => {
            return (
              <Capsule
                key={option.id}
                label={t(option.label)}
                selected={option.selected}
                onClick={(e) => {
                  toggleOption(option.id);
                }}
              />
            );
          })}
        </div>
        {otherOption?.selected && (
          <OtherChipsInput
            chips={otherOption.otherTexts ?? []}
            inputText={otherOption.otherInputText ?? ""}
            onInputChange={(v) => setOtherInputText(otherOption.id, v)}
            onCommit={() => commitOtherChip(otherOption.id)}
            onRemoveChip={(i) => removeOtherChip(otherOption.id, i)}
          />
        )}
      </div>
      {/* 푸터 규약(`flex-wrap`·`gap-3`·단일 버튼 초과 한계)은 첫 스텝 주석 참조. */}
      <div className="mt-10 flex shrink-0 flex-wrap items-center justify-end gap-3">
        <StepCard.Prev className="bg-white text-blue-60" onClick={prev}>
          {t("Back")}
        </StepCard.Prev>
        <StepCard.Next disabled={!someCurrentStepSelected} onClick={next} />
      </div>
    </StepCard>
  );
};

// 세번째(마지막)
const SelectFeatures = ({ onReady }: { onReady: () => void }) => {
  const {
    steps,
    toggleOption,
    setOtherInputText,
    commitOtherChip,
    removeOtherChip,
    isOtherOption,
    prev,
    reportRecommendedTactSuits,
    someCurrentStepSelected,
  } = useRecommendTactSuitStore();
  const { t } = useTrans(["gnb"]);
  const { token } = useUser();
  const router = useRouter();
  const otherOption = steps[2].options.find((o) => isOtherOption(o.id));

  return (
    <StepCard
      numStages={3}
      currentStage={2}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="mt-6 shrink-0 laptop:mt-12">
        <StepCard.Stepper />
        <Label className="mt-11 laptop:mt-16">
          {t("Pick up two features that are most important to you?")}
        </Label>
        <Description className="mt-3 laptop:mt-4">
          {t("Select at least one")}
        </Description>
      </div>
      {/* 스크롤 영역 = 선택지 + Other 칩 입력. 칩 입력을 **이 안에** 둔다 —
          바깥 형제로 두면 `min-h-0` 이 붙은 이 블록만 수축 가능해서 음수 공간을 전부
          흡수한다(실측 360x568·칩 4개: 본문이 248 → 59px 로 짜부라짐). 칩 입력은
          "선택" UI 라 선택지와 함께 스크롤되는 게 의미상으로도 맞다. */}
      <div className="mt-10 flex min-h-0 flex-1 flex-col overflow-y-auto laptop:mt-16">
        <div className="flex flex-wrap gap-3">
          {steps[2].options.map((option) => {
            return (
              <Capsule
                key={option.id}
                label={t(option.label)}
                selected={option.selected}
                onClick={(e) => {
                  toggleOption(option.id);
                }}
              />
            );
          })}
        </div>
        {otherOption?.selected && (
          <OtherChipsInput
            chips={otherOption.otherTexts ?? []}
            inputText={otherOption.otherInputText ?? ""}
            onInputChange={(v) => setOtherInputText(otherOption.id, v)}
            onCommit={() => commitOtherChip(otherOption.id)}
            onRemoveChip={(i) => removeOtherChip(otherOption.id, i)}
          />
        )}
      </div>
      {/* 푸터 규약(`flex-wrap`·`gap-3`·단일 버튼 초과 한계)은 첫 스텝 주석 참조. */}
      <div className="mt-10 flex shrink-0 flex-wrap items-center justify-end gap-3">
        <StepCard.Prev className="bg-white text-blue-60" onClick={prev}>
          {t("Back")}
        </StepCard.Prev>
        <ActionButton
          variant={"blue-fill"}
          shape={"pill"}
          disabled={!someCurrentStepSelected}
          onClick={() => {
            reportRecommendedTactSuits({
              locale: router.locale ?? "",
              bearerToken: token?.token,
            });
            onReady();
          }}
        >
          {t("View Recommendations")}
        </ActionButton>
      </div>
    </StepCard>
  );
};

const Intro = ({
  onClick,
  disableAnimation = false,
}: {
  onClick?: () => void;
  disableAnimation?: boolean;
}) => {
  const { t } = useTrans(["gnb"]);
  const { ref, className, delayFunc } = useStaggerAnimation({
    disabled: disableAnimation,
  });

  return (
    <div
      ref={ref}
      onClick={() => {
        onClick?.();
      }}
      className={cn("w-full cursor-pointer", className)}
      style={{ transitionDelay: delayFunc(0) }}
    >
      <div className="relative mx-auto max-w-screen-max overflow-hidden rounded-2xl laptop:h-[400px]">
        <div className="hidden h-full items-center pl-16 laptop:flex">
          <div className="absolute inset-0">
            <Image
              src={`${BASE_PATH}/01_desktop@2x.jpg`}
              alt="Recommend TactSuit"
              fill
              className="object-cover"
            />
          </div>
          <div className="relative z-10">
            <Title>{t("Which TactSuit is right for you?")}</Title>
            <Button variant={"blue-fill"} shape={"pill"} className="mt-6 !px-6">
              {t("Find Yours")}
            </Button>
          </div>
        </div>
        <div className="laptop:aspect-none relative aspect-[328/240]">
          <Image
            src={`${BASE_PATH}/01_mobile@3x.jpg`}
            alt="Recommend TactSuit"
            fill
            className="object-cover"
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-y-6 bg-coolgray-10 p-6 laptop:hidden">
          <Title className="text-center">
            {t("Which TactSuit is right for you?")}
          </Title>
          <Button
            onClick={() => {
              onClick?.();
            }}
            variant={"blue-fill"}
            shape={"pill"}
            className="!min-h-0 !px-6 !py-3 !text-sm laptop:mt-6"
          >
            {t("Find Yours")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Label = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <h4 className={cn("text-2xl text-coolgray-90 laptop:text-4xl", className)}>
      {children}
    </h4>
  );
};

const Title = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <h4 className={cn("text-3xl text-coolgray-90 laptop:text-5xl", className)}>
      {children}
    </h4>
  );
};

const Description = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <h4 className={cn("text-lg text-coolgray-60", className)}>{children}</h4>
  );
};

const ActionButton = ({
  children,
  className,
  ...rest
}: React.PropsWithChildren<ButtonProps>) => {
  return (
    <Button {...rest} className={cn("min-h-0 px-5 py-3 text-sm", className)}>
      {children}
    </Button>
  );
};

/**
 * 스크롤 컨테이너가 **아직 더 남았는지** 알려준다(#5298).
 *
 * 추천 결과 그리드는 넘치면 자기 안에서 스크롤되는데, 표시가 없어서 사용자가 아래에 제품
 * 상세 링크가 더 있다는 걸 모른다 — 실측(de, 849x579): 보이는 높이 285 vs 실제 963
 * (**숨은 높이 678 = 70%**), `mehr erfahren` 링크 2 개가 **둘 다 화면 밖**이었다.
 *
 * **양쪽에 2px 여유**를 둔다 — `scrollHeight > clientHeight + 2` 이고 끝 판정도
 * `scrollHeight - 2` 다. 소수점 스크롤 위치(브라우저 확대·고DPI)에서 1px 오차로 힌트가
 * 깜빡이는 걸 막는다. 끝까지 내리면 힌트를 내려 "더 있다" 는 거짓 신호를 남기지 않는다.
 *
 * ⚠️ `onScroll` 만으로는 부족하다 — 팝업이 열릴 때/제품 수가 바뀔 때/창 크기가 바뀔 때는
 * 스크롤 이벤트가 안 나므로 초기값이 굳는다. `ResizeObserver` 로 콘텐츠·컨테이너 변화도 본다.
 *
 * ⚠️ **`deps` 를 받는 이유** — effect 는 마운트 시점의 자식만 관찰한다. 그런데 소비처의
 * `products` 는 `useProducts()`(React Query) 기반이라 **비어 있는 채로 마운트될 수 있다**
 * (`filterRecommendation` 이 `data === undefined` 면 전부 걸러낸다). 그 뒤 쿼리가 resolve 되어
 * 자식이 생겨도
 *   - 관찰 대상이 0개였으므로 자식 쪽 RO 가 없고,
 *   - `ro.observe(el)` 은 `el` **자신의 박스**만 보는데 `el` 은 고정 높이 래퍼 안의 stretched
 *     grid item 이라 자식이 늘어도 박스가 안 변한다
 * → RO 미발동 + effect 재실행 없음 = `hasMore` 가 `false` 로 굳는다. 이 힌트가 고치려는 바로
 * 그 상태다. `deps` 로 effect 를 다시 돌려 **재측정 + 재관찰**을 함께 한다.
 * (`measure()` 만 다시 부르면 그 이후의 카드 높이 변화를 다시 놓친다.)
 */
export const useHasMoreBelow = <T extends HTMLElement>(
  deps: unknown[] = [],
) => {
  const ref = useRef<T>(null);
  const [hasMore, setHasMore] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setHasMore(el.scrollHeight > el.clientHeight + 2 && !atEnd);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();

    el.addEventListener("scroll", measure, { passive: true });
    // jsdom 등 ResizeObserver 가 없는 환경에서는 resize 로 대체한다(폴백 없으면 초기값에 굳는다).
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => {
        el.removeEventListener("scroll", measure);
        window.removeEventListener("resize", measure);
      };
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // 콘텐츠(카드) 높이 변화도 잡아야 한다 — 컨테이너 크기는 그대로인데 안이 커질 수 있다.
    Array.from(el.children).forEach((c) => ro.observe(c));
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  return { ref, hasMore };
};

const SuggestProducts = ({ onClose }: { onClose?: () => void }) => {
  const { recommendation, clear } = useRecommendTactSuitStore();
  const { t } = useTrans(["gnb"]);
  const { products } = recommendation;
  // products 는 useProducts() 기반이라 마운트 후에 채워질 수 있다 — 그때 다시 재고,
  // **새로 생긴 자식도 다시 관찰해야** 한다(훅 docblock 의 `deps` 설명 참조).
  const { ref: gridRef, hasMore } = useHasMoreBelow<HTMLDivElement>([
    products.length,
  ]);

  return (
    <div className="flex min-h-0 grow flex-col overflow-hidden">
      <Label className="mt-6">
        {t("Recommended TactSuit for you:", { nsSeparator: false })}
      </Label>
      {/* 그리드를 감싸는 relative 컨테이너 — 힌트를 그리드 하단에 오버레이한다.
          `grow`/`min-h-0` 은 이 래퍼가 받고 그리드는 래퍼를 꽉 채운다. */}
      <div className="relative mt-10 grid min-h-0 grow">
        <div
          ref={gridRef}
          className="grid auto-cols-fr gap-x-2 gap-y-4 overflow-auto rounded-2xl laptop:grid-flow-col laptop:bg-coolgray-10 laptop:p-4"
        >
          {products.map(({ product, isBestMatch }, index) => {
            if (products.length === 1) {
              return (
                <FullCard
                  key={product?.id || index}
                  product={product!}
                  isBestMatch={isBestMatch}
                />
              );
            }
            return (
              <Card
                key={product?.id || index}
                product={product!}
                isBestMatch={isBestMatch}
              />
            );
          })}
        </div>

        {/* 스크롤 힌트 — 아래에 더 있을 때만 뜨고 끝에 닿으면 사라진다.
            `pointer-events-none` 이라 카드 클릭을 막지 않는다.
            페이드(그라디언트)를 함께 둔다 — 화살표만으로는 눈에 덜 걸리고, 마지막 카드가
            **덮이는 만큼이 "아래로 이어진다" 는 신호**가 된다. 빼본 뒤 비교해서 남기기로 했다. */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 flex h-16 items-end justify-center",
            "rounded-b-2xl bg-gradient-to-t from-white/95 to-transparent",
            "transition-opacity duration-200 laptop:from-coolgray-10",
            hasMore ? "opacity-100" : "opacity-0",
          )}
        >
          <svg
            width="21.37"
            height="12.04"
            viewBox="0 0 21.3677 12.0425"
            fill="none"
            className="mb-2 text-coolgray-70 motion-safe:animate-bounce"
          >
            <path
              d="M10.6792 12.0425C10.4382 12.0425 10.2139 12.004 10.0062 11.927C9.79854 11.85 9.60104 11.718 9.4137 11.531L0.425203 2.5425C0.148537 2.2655 0.00686993 1.91733 0.000203266 1.498C-0.00613007 1.079 0.135537 0.7245 0.425203 0.4345C0.715203 0.144833 1.06654 0 1.4792 0C1.89187 0 2.2432 0.144833 2.5332 0.4345L10.6792 8.581L18.8252 0.4345C19.1022 0.157833 19.4504 0.0161668 19.8697 0.00950012C20.2887 0.00316679 20.6432 0.144833 20.9332 0.4345C21.2229 0.7245 21.3677 1.07583 21.3677 1.4885C21.3677 1.90117 21.2229 2.2525 20.9332 2.5425L11.9447 11.531C11.7574 11.718 11.5599 11.85 11.3522 11.927C11.1445 12.004 10.9202 12.0425 10.6792 12.0425Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
      {/* 푸터 규약(`flex-wrap`·`gap-3`·단일 버튼 초과 한계)은 첫 스텝 주석 참조. */}
      <div className="mt-10 flex shrink-0 flex-wrap items-center justify-end gap-3">
        <ActionButton
          onClick={() => {
            onClose?.();
            clear();
          }}
          variant={"darkgray-outline"}
          shape={"pill"}
        >
          {t("Retry")}
        </ActionButton>
        <Link href={`/tactsuit/compare`}>
          <ActionButton shape={"pill"} variant={"darkgray-fill"}>
            {t("See detailed comparison")}
          </ActionButton>
        </Link>
      </div>
    </div>
  );
};

const Card = ({
  className,
  product,
  isBestMatch,
}: {
  className?: string;
  product: Product;
  isBestMatch: boolean;
}) => {
  const { t } = useTrans(["tactsuit"]);

  return (
    <div
      className={cn(
        "flex flex-col items-center rounded-2xl p-4",
        isBestMatch ? "bg-coolgray-10 laptop:bg-white" : "bg-coolgray-10",
        isBestMatch
          ? "shadow-none laptop:[box-shadow:0px_2px_4px_0px_#00000026]"
          : "",
        className,
      )}
    >
      <Badge
        variant={"best"}
        className={cn(
          "h-4 text-xs laptop:h-5 laptop:text-sm",
          !isBestMatch && "opacity-0",
        )}
      >
        {isBestMatch ? "Best Match" : ""}
      </Badge>
      <TactSuitCard product={product}>
        <div className="mt-2 w-full">
          <div className="relative aspect-square">
            <TactSuitCard.Thumbnail fill />
          </div>
          <div className="mt-2 text-center">
            <TactSuitCard.Name className="text-base font-500" />
          </div>
          <div className="mt-4 flex justify-center">
            <div className="grid auto-cols-fr grid-flow-row gap-2">
              <TactSuitCard.NotifyOrBuy
                allowBundleSuggestion={false}
                variant={"blue-fill"}
                className="px-4"
              />
              <TactSuitCard.LearnMore
                variant={"blue-outline"}
                className="rounded-full px-4"
              />
            </div>
          </div>
          <ul className="ml-4 mt-4 list-outside list-disc">
            <li className="">{t(product.desc)}</li>
          </ul>
        </div>
      </TactSuitCard>
    </div>
  );
};

const FullCard = ({
  className,
  product,
  isBestMatch,
}: {
  className?: string;
  product: Product;
  isBestMatch: boolean;
}) => {
  const { t } = useTrans(["tactsuit"]);

  return (
    <Fragment>
      <div
        className={cn(
          "hidden items-center rounded-2xl p-4 laptop:flex",
          isBestMatch ? "bg-white" : "bg-coolgray-10",
          isBestMatch
            ? "shadow-none laptop:[box-shadow:0px_2px_4px_0px_#00000026]"
            : "",
          className,
        )}
      >
        <TactSuitCard product={product}>
          <div className="grid auto-cols-fr grid-flow-col items-center">
            <div className="relative aspect-square">
              <TactSuitCard.Thumbnail fill />
            </div>
            <div className="flex flex-col items-start justify-center gap-y-3">
              {!isBestMatch && (
                <Badge
                  variant={"best"}
                  className={cn("h-4 text-xs laptop:h-5 laptop:text-sm")}
                >
                  {"Best Match"}
                </Badge>
              )}
              <TactSuitCard.Name className="text-base font-500" />
              <div className="mt-6 flex flex-wrap gap-2">
                <TactSuitCard.NotifyOrBuy
                  allowBundleSuggestion={false}
                  variant={"blue-fill"}
                  className="px-4"
                />
                <TactSuitCard.LearnMore className="px-6" />
                <ul className="ml-4 mt-10 list-outside list-disc">
                  <li className="">{t(product.desc)}</li>
                </ul>
              </div>
            </div>
          </div>
        </TactSuitCard>
      </div>
      <Card
        className="laptop:hidden"
        product={product!}
        isBestMatch={isBestMatch}
      />
    </Fragment>
  );
};
